import { useCallback, useEffect, useMemo, useState } from "react";
import { ethers } from "ethers";
import FlowModal from "../components/FlowModal.jsx";
import ResultModal from "../components/ResultModal.jsx";
import { StatCard, STAT_ICONS } from "../components/StatRow.jsx";
import config from "../config/index.js";
import { useWallet } from "../context/WalletContext.jsx";
import { upsertDeposit } from "../services/api.js";
import { getContract } from "../services/contracts.js";
import { getTxOverrides } from "../services/gas.js";
import { decodeContractError } from "../utils/contractError.js";
import { parseUsdc, formatUsdcCompact } from "../utils/format.js";

const TOKEN_SYMBOL = "USDC";
const QUICK_AMOUNTS = [100, 500, 1000, 5000, 10000];

// Explicit gas limit for the §V6.3 Turbo entry (openTurboPosition). The call
// loops up to 12 leverage rounds (each: Uniswap LP top-up + market buy + loan
// + fresh deposit); worst-case execution measured at ~8.5M gas, but the
// wallet's eth_estimateGas inflates it to ~20-25M via EIP-150's 63/64
// gas-forwarding rule across the deep looped call tree, tripping the ~16.77M
// block-gas cap and rejecting the deposit before it runs. Hard-coding a limit
// well above real usage (~11M incl. forwarding headroom) and under the cap
// makes the wallet skip its broken estimate. Standard deposits are a single
// call and don't need this.
const TURBO_POSITION_GAS_LIMIT = 16_000_000n;

/**
 * §I.6 pool-type preference, set at deposit-open time. Values mirror
 * the on-chain compoundMode flag (0=Off, 1=Standard, 2=Turbo).
 *
 *  Off      → this deposit cannot be attached to any Automated Reinvestment pool;
 *             it just earns the standard 20% APR in isolation.
 *  Standard → eligible for a Standard pool (yield-only sweep, mints a
 *             plain $100 deposit when the pool clears 102 USDC).
 *  Turbo    → eligible for a Turbo pool (yield + leverage sweep, mints
 *             a $160.00 leveraged deposit per $100 swept).
 *
 * Turbo is pre-selected because it is the protocol-optimal setting.
 */
// §I.6 Product selector — Turbo OR Standard. "Off" is no longer a
// product choice; instead the separate Automated Reinvestment toggle below
// determines whether the deposit participates in the chosen pool's
// sweep cycle. When toggled OFF, the on-chain compoundMode is forced
// to 0 regardless of the picker (deposit just accrues simple interest
// and can be withdrawn anytime at maturity).
const PRODUCT_OPTIONS = [
    {
        value: 2,
        label: "Turbo",
        rate: "up to 27.5%",
        hint: "Recommended",
        copy: "Turbo: interest is leveraged back into new deposits for maximum yield. The full 27.5% only applies to larger deposits (~$10k+); smaller deposits earn less (e.g. $1k ≈ 25%, $100 ≈ 11%) because the leverage loop stops at the $100 minimum."
    },
    {
        value: 1,
        label: "Standard",
        rate: "20%",
        hint: "80% Capital Protected",
        copy: "Standard: yield reinvests linearly. No leverage."
    }
];

/**
 * Deposit creation flow. Walks the user through approval and openDeposit
 * with a styled multi-step modal. While MetaMask prompts run in the
 * background, the on-screen flow modal updates each step's status. On
 * success the flow modal closes and a result modal points to the
 * Dashboard. On error the flow modal closes and the result modal surfaces
 * the failure.
 */
export default function Deposit() {
    const { address, signer } = useWallet();
    const [amount, setAmount] = useState("");
    const [agreed, setAgreed] = useState(false);
    // §I.6 Product (Turbo=2 / Standard=1). Defaults to Turbo because it
    // is the protocol-optimal setting.
    const [productMode, setProductMode] = useState(2);
    // Automated Reinvestment participation toggle. When ON, the on-chain
    // compoundMode is set to `productMode` (so the deposit can attach
    // to the matching pool). When OFF, compoundMode stays 0 and the
    // deposit just accrues simple interest until withdrawal.
    const [autoCompoundOn, setAutoCompoundOn] = useState(true);
    // Derived compoundMode that actually goes to the chain.
    const compoundMode = autoCompoundOn ? productMode : 0;
    const [busy, setBusy] = useState(false);
    // Live USDC balance for the connected wallet in base units (6 dp).
    // Used to clamp the input + the Max button + the "Balance:" hint.
    const [balance, setBalance] = useState(0n);

    const [flowOpen, setFlowOpen] = useState(false);
    const [steps, setSteps] = useState(buildInitialSteps("", 2));
    const [result, setResult] = useState(null);

    const numeric = Number(amount || 0);
    const split = useMemo(() => previewSplit(numeric, productMode), [numeric, productMode]);
    const projected = useMemo(() => projectMaturity(numeric, productMode), [numeric, productMode]);
    const belowMin = numeric > 0 && numeric < config.protocol.minDepositUsdc;
    // Convert the BigInt balance to its USDC representation (truncated to 6dp)
    // so we can compare against the user's string input and feed the "Max"
    // button without floating-point drift on large balances.
    const balanceUsdcStr = useMemo(() => baseToUsdcString(balance), [balance]);
    const balanceUsdcNum = Number(balanceUsdcStr);
    const exceedsBalance = numeric > 0 && numeric > balanceUsdcNum;
    const insufficientBalance = numeric >= config.protocol.minDepositUsdc && exceedsBalance;

    const refreshBalance = useCallback(async () => {
        if (!address) {
            setBalance(0n);
            return;
        }
        try {
            // Prefer the signer's provider so the read uses the same RPC the
            // user is already paying for (no extra public-RPC round-trip).
            const runner = signer || null;
            if (!runner) return;
            const usdc = getContract("usdc", runner);
            const b = await usdc.balanceOf(address);
            setBalance(BigInt(b));
        } catch (err) {
            console.warn("USDC balance fetch failed:", err?.message || err);
        }
    }, [address, signer]);

    useEffect(() => {
        refreshBalance();
    }, [refreshBalance]);

    function setStepStatus(key, status) {
        setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, status } : s)));
    }

    async function handleDeposit() {
        if (!signer) return;
        setBusy(true);
        const usdc = getContract("usdc", signer);
        const bond = getContract("bond", signer);
        // §V6.3 — Turbo routes through the Turbo contract's ONE-TX entry
        // (openTurboPosition), which opens the deposit, sets auto-reinvest AND
        // runs the recursive leverage loop in a single signed tx. Standard
        // still uses bond.openDeposit. USDC is therefore approved to whichever
        // contract actually pulls it.
        const isTurbo = productMode === 2;
        const turbo = isTurbo ? getContract("turbo", signer) : null;
        const spender = isTurbo ? config.contracts.turbo : config.contracts.bond;
        const amountWei = parseUsdc(amount);
        // Read allowance BEFORE building the flow steps so we can hide
        // the "Approving USDC" step entirely when an infinite approval
        // is already in place — the user doesn't need to be told about
        // a wallet prompt that will never appear.
        let approveNeeded = true;
        try {
            const allowance = await usdc.allowance(address, spender);
            if (allowance >= amountWei) approveNeeded = false;
        } catch {
            // Allowance read failed (RPC blip) — assume approve needed
            // and let the explicit approve tx surface any real issue.
        }
        const initialSteps = buildInitialSteps(amount, compoundMode, approveNeeded);
        setSteps(initialSteps);
        setFlowOpen(true);
        setResult(null);

        try {
            const overrides = await getTxOverrides(signer.provider);

            if (approveNeeded) {
                setStepStatus("approve", "loading");
                // Infinite approval — user approves ONCE per wallet ×
                // BondContract pair, never sees the prompt again on
                // subsequent deposits. Mirrors how Lending + Reinvest
                // approvals already work.
                const approvalTx = await usdc.approve(
                    spender,
                    ethers.MaxUint256,
                    overrides
                );
                await approvalTx.wait();
                await waitForAllowance(usdc, address, spender, amountWei);
                setStepStatus("approve", "done");
            }

            setStepStatus("deposit", "loading");
            let receipt;
            let depositTx;
            let compoundWarning = "";
            if (isTurbo) {
                // §V6.3 — ONE tx: opens the initial Turbo deposit, sets
                // auto-reinvest (compoundMode), AND runs the recursive
                // leverage loop. No separate openDeposit / setCompoundMode /
                // executeTurboLoop calls.
                //
                // openTurboPosition runs up to 12 leverage rounds, each doing
                // a Uniswap LP top-up + market buy + loan + fresh deposit. The
                // tx only CONSUMES ~8.5M gas at the worst case (12 rounds), but
                // the wallet's eth_estimateGas over-estimates it to ~20-25M
                // because of EIP-150's 63/64 gas-forwarding rule compounding
                // across this deep, looped call tree. That inflated estimate
                // trips the node/wallet block-gas cap (~16.77M) and the deposit
                // is rejected before it ever runs ("missing revert data"). We
                // therefore pass an explicit gasLimit — comfortably above real
                // usage (~11M needed incl. the 63/64 headroom) and safely under
                // the cap — so the wallet skips its broken estimate.
                depositTx = await turbo.openTurboPosition(amountWei, compoundMode, {
                    ...overrides,
                    gasLimit: TURBO_POSITION_GAS_LIMIT
                });
                receipt = await depositTx.wait();
            } else {
                // Standard product (contract enum 0).
                depositTx = await bond.openDeposit(amountWei, 0, overrides);
                receipt = await depositTx.wait();
            }
            setStepStatus("deposit", "done");
            // Resolve the new depositId (first DepositOpened in the receipt) —
            // used for the backend fast-path below. For Turbo this is the
            // initial position; the loop rungs arrive via the indexer.
            const depositId = await resolveDepositId(bond, receipt);

            if (isTurbo) {
                // compoundMode + loop were applied inside openTurboPosition.
                if (compoundMode !== 0) setStepStatus("compound", "done");
            } else if (compoundMode !== 0 && depositId) {
                // §I.6 — Standard: apply the Automated Reinvestment pool-type
                // preference via a follow-up setCompoundMode tx.
                setStepStatus("compound", "loading");
                try {
                    // Race guard: `depositTx.wait()` only means the dApp's
                    // provider saw the deposit. The RPC the WALLET uses to
                    // simulate (eth_estimateGas) the follow-up
                    // setCompoundMode can still be a block or two behind,
                    // making `_existingDeposit` revert. Wait until the
                    // deposit reads back so every RPC has caught up first.
                    await waitForDepositVisible(bond, depositId);
                    const cmTx = await bond.setCompoundMode(depositId, compoundMode, overrides);
                    await cmTx.wait();
                    setStepStatus("compound", "done");
                } catch (cmErr) {
                    console.warn("Pool-type preference failed:", cmErr?.message || cmErr);
                    setStepStatus("compound", "error");
                    compoundWarning =
                        " Pool-type preference could not be set automatically. Change it anytime from Settings before attaching to a pool.";
                }
            }

            // Fast-path: tell the backend about the new deposit and WAIT
            // for it — so My Term Deposits shows the row the moment the
            // user navigates there, instead of waiting for the next
            // indexer tick. Idempotent: the indexer's later write
            // collides on the unique depositId index and is a no-op.
            await recordDepositToBackend({
                bond,
                depositId,
                receipt,
                owner: address,
                amountWei,
                txHash: depositTx.hash
            });

            await delay(300);
            setFlowOpen(false);
            setResult({
                type: "success",
                title: "Deposit Successful",
                message: `Your ${amount} ${TOKEN_SYMBOL} deposit is now active.${compoundWarning} View its lifecycle in My Term Deposits.`
            });
            setAmount("");
            // Balance just decreased by `amountWei` on chain; refresh so the
            // hint + Max button reflect the new figure without a page reload.
            refreshBalance();
        } catch (err) {
            setSteps((prev) =>
                prev.map((s) => (s.status === "loading" ? { ...s, status: "error" } : s))
            );
            await delay(300);
            setFlowOpen(false);
            setResult({
                type: "error",
                title: "Deposit Failed",
                message: decodeContractError(err)
            });
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="-mt-6 space-y-3">
            {/* Term highlights — 4-up StatCard row. §V6.3 — figures follow
                the selected product: Standard 20% APR / 80% SV / 20% penalty
                vs Turbo (leveraged 2.5x) net 27.5% APR / 50% SV / 50% penalty,
                so the header row never contradicts the picker below. */}
            <TermHighlights productMode={productMode} />

            {/* Safeguards — full-width strip, 4 bullets in one row.
                Bullet copy adapts to the picked product for the same
                reason as TermHighlights above. */}
            <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] px-4 py-2.5">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <h3 className="shrink-0 font-manrope text-[12px] font-bold uppercase tracking-widest text-[#e6c985] sm:pr-4 sm:border-r sm:border-white/20">
                        Safeguards
                    </h3>
                    <ul className="grid flex-1 grid-cols-2 gap-x-4 gap-y-1.5 text-[10.5px] leading-snug text-white sm:grid-cols-3 sm:pl-4">
                        <Bullet>3-year fixed term, {productMode === 2 ? "up to 27.5" : "20"}% APR.</Bullet>
                        <Bullet>{productMode === 2 ? "50" : "20"}% early-exit penalty.</Bullet>
                        <Bullet>Fully Transferable (coming soon).</Bullet>
                    </ul>
                </div>
            </div>

            {/* Main row — 2 columns: Deposit Amount · (Projected at Maturity over Capital Routing) */}
            <div className="grid gap-3 lg:grid-cols-12 items-stretch">
                {/* Deposit Amount — left column */}
                <div className="lg:col-span-6 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 flex flex-col">
                    <div className="flex items-center justify-between">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                            Deposit Amount
                        </h3>
                        <span className="text-[9px] font-semibold uppercase tracking-widest text-[#e6c985]/70">
                            Min ${config.protocol.minDepositUsdc} {TOKEN_SYMBOL}
                        </span>
                    </div>

                    <div className="mt-3 flex items-center gap-2 rounded-[10px] bg-[#162e1f] ring-1 ring-[#bfb67b] px-3 py-1.5">
                        <span className="font-manrope text-[18px] font-bold text-white/45">
                            $
                        </span>
                        <input
                            id="amount"
                            type="number"
                            min={config.protocol.minDepositUsdc}
                            step="1"
                            maxLength={10}
                            className="flex-1 min-w-0 bg-transparent font-manrope text-[20px] font-bold text-white placeholder:text-white/30 focus:outline-none [color-scheme:dark]"
                            value={amount}
                            placeholder="0"
                            onChange={(event) => {
                                const next = event.target.value;
                                // type="number" ignores maxLength natively, so we
                                // enforce the 10-char cap in JS. Allow empty input
                                // so users can clear the field.
                                if (next.length > 10) return;
                                // Cap to live wallet balance so users can't even
                                // type more than they have. Empty / partial inputs
                                // pass straight through.
                                if (next === "" || next === ".") {
                                    setAmount(next);
                                    return;
                                }
                                const parsedNum = Number(next);
                                if (
                                    Number.isFinite(parsedNum) &&
                                    parsedNum > balanceUsdcNum &&
                                    balanceUsdcNum > 0
                                ) {
                                    setAmount(balanceUsdcStr);
                                    return;
                                }
                                setAmount(next);
                            }}
                        />
                        <span className="rounded-md bg-[#FBC206] px-2 py-0.5 text-[10px] font-semibold text-[#10281b]">
                            {TOKEN_SYMBOL}
                        </span>
                    </div>

                    {/* Balance hint + Max button */}
                    <div className="mt-1.5 flex items-center justify-between text-[10px] text-[#e6c985]/70">
                        <span>
                            Balance:{" "}
                            <span className="font-semibold text-white">
                                ${formatUsdcCompact(balance)} {TOKEN_SYMBOL}
                            </span>
                        </span>
                        <button
                            type="button"
                            disabled={balance === 0n}
                            onClick={() => setAmount(balanceUsdcStr)}
                            className="rounded-md bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-[#e6c985] hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Max
                        </button>
                    </div>

                    {insufficientBalance ? (
                        <p className="mt-1.5 text-[10px] font-medium text-[#C93030]">
                            Insufficient balance. You hold ${formatUsdcCompact(balance)}{" "}
                            {TOKEN_SYMBOL}.
                        </p>
                    ) : belowMin ? (
                        <p className="mt-1.5 text-[10px] font-medium text-[#C93030]">
                            Minimum deposit is ${config.protocol.minDepositUsdc} {TOKEN_SYMBOL}.
                        </p>
                    ) : null}

                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {QUICK_AMOUNTS.map((q) => {
                            const active = numeric === q;
                            // Disable any quick-pick chip the user can't actually
                            // afford — better than letting them click and getting
                            // an instant "Insufficient balance" error.
                            const tooExpensive = q > balanceUsdcNum;
                            return (
                                <button
                                    key={q}
                                    type="button"
                                    disabled={tooExpensive}
                                    onClick={() => setAmount(String(q))}
                                    className={`rounded-md px-2 py-0.5 text-[9.5px] font-semibold transition-colors ${
                                        active
                                            ? "bg-[#FBC206] text-[#10281b]"
                                            : "bg-white/10 text-white ring-1 ring-[#bfb67b] hover:bg-white/20"
                                    } disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white/10`}
                                >
                                    ${q.toLocaleString()}
                                </button>
                            );
                        })}
                    </div>

                    {/* Product selector — Turbo pre-selected (protocol-optimal).
                        Users opt down to Standard / Off explicitly. */}
                    <div className="mt-3">
                        <div className="flex items-center justify-between">
                            <h4 className="font-manrope text-[10px] font-bold uppercase tracking-widest text-[#e6c985]">
                                Select Product
                            </h4>
                            <span className="text-[9px] font-medium text-[#e6c985]/70">
                                Pool eligibility at sweep time
                            </span>
                        </div>
                        {/* §I.6 Product picker — 2 options only (Turbo / Standard). */}
                        <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                            {PRODUCT_OPTIONS.map((opt) => {
                                const active = productMode === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setProductMode(opt.value)}
                                        className={`rounded-[8px] px-2.5 py-2 text-left ring-1 transition-colors ${
                                            active
                                                ? "bg-[#FBC206] ring-[#FBC206]"
                                                : "bg-[#162e1f] ring-[#bfb67b] hover:bg-white/5"
                                        }`}
                                    >
                                        <div className="flex items-baseline justify-between gap-1">
                                            <span
                                                className={`text-[11px] font-bold ${
                                                    active ? "text-[#10281b]" : "text-white"
                                                }`}
                                            >
                                                {opt.label}
                                            </span>
                                            <span
                                                className={`text-[10px] font-semibold ${
                                                    active ? "text-[#10281b]" : "text-[#FBC206]"
                                                }`}
                                            >
                                                {opt.rate}
                                            </span>
                                        </div>
                                        <span
                                            className={`block mt-0.5 text-[8.5px] font-medium leading-tight ${
                                                active ? "text-[#10281b]/70" : "text-[#e6c985]/60"
                                            }`}
                                        >
                                            {opt.hint}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        <p className="mt-1 text-[9px] text-[#e6c985]/70">
                            {PRODUCT_OPTIONS.find((o) => o.value === productMode)?.copy}
                        </p>
                    </div>

                    {/* §I.6 Automated Reinvestment toggle — separate from product picker.
                        ON  → deposit attaches to matching pool, swept at $100 of
                              combined yield, mints a new 3-year TD.
                        OFF → deposit just accrues simple interest; user withdraws
                              at maturity (or any time after, no penalty). */}
                    <div className="mt-3 flex items-start justify-between gap-2 rounded-[8px] bg-white/5 ring-1 ring-[#bfb67b]/50 px-3 py-2">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="font-manrope text-[11px] font-bold text-white">
                                    Automated Reinvestment
                                </span>
                                <span
                                    className={`text-[9px] font-semibold uppercase tracking-widest ${
                                        autoCompoundOn ? "text-[#22C55E]" : "text-[#e6c985]/60"
                                    }`}
                                >
                                    {autoCompoundOn ? "On" : "Off"}
                                </span>
                            </div>
                            <p className="mt-0.5 text-[9.5px] leading-snug text-[#e6c985]/75">
                                {autoCompoundOn
                                    ? "When pool yield reaches $100, the protocol automatically sweeps and mints a new 3-year Term Deposit for you."
                                    : "Interest accrues in your deposit. Withdraw penalty-free at maturity or any time after; exiting earlier forfeits the early-exit penalty."}
                            </p>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={autoCompoundOn}
                            onClick={() => setAutoCompoundOn((prev) => !prev)}
                            className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                                autoCompoundOn ? "bg-[#FBC206]" : "bg-white/15"
                            }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                    autoCompoundOn ? "translate-x-[18px]" : "translate-x-0.5"
                                }`}
                            />
                        </button>
                    </div>

                    <label className="mt-3 flex items-start gap-1.5 rounded-[8px] bg-white/10 ring-1 ring-[#bfb67b] px-2 py-1.5 text-[9.5px] leading-snug text-white">
                        <input
                            type="checkbox"
                            checked={agreed}
                            onChange={(event) => setAgreed(event.target.checked)}
                            className="mt-0.5 h-3 w-3 accent-[#FBC206]"
                        />
                        <span>
                            Not a resident of restricted jurisdictions (US, SG); I accept the{" "}
                            <span className="font-semibold text-[#e6c985]">
                                {productMode === 2 ? "50% early-exit penalty" : "20% early-exit penalty"}
                            </span>
                            .
                        </span>
                    </label>

                    <button
                        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#FBC206] px-4 py-2.5 font-manrope text-[13px] font-semibold text-[#10281b] hover:-translate-y-0.5 hover:shadow-md transition-all disabled:cursor-not-allowed disabled:opacity-50 disabled:translate-y-0 disabled:hover:shadow-none"
                        disabled={
                            busy ||
                            !signer ||
                            !agreed ||
                            numeric < config.protocol.minDepositUsdc ||
                            exceedsBalance
                        }
                        onClick={handleDeposit}
                    >
                        {busy
                            ? "Submitting..."
                            : !signer
                            ? "Connect Wallet to Continue"
                            : exceedsBalance
                            ? "Insufficient Balance"
                            : "Initiate Deposit"}
                        <ChevronRightSmall />
                    </button>
                </div>

                {/* Right column — Projected at Maturity on top, Capital Routing below */}
                <div className="lg:col-span-6 flex flex-col gap-3">
                    {/* Projected at Maturity */}
                    <div className="flex flex-1 flex-col rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 text-white">
                        <div className="flex items-center justify-between">
                            <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                                Projected at Maturity
                            </h3>
                            <span className="inline-flex items-center rounded-full bg-white/10 ring-1 ring-[#bfb67b]/40 px-2 py-0.5 text-[9px] font-semibold text-[#FBC206]">
                                {config.protocol.fixedTermDays}d · {projected.aprLabel}% APR
                            </span>
                        </div>

                        <div className="mt-3 grid flex-1 grid-rows-2 gap-2">
                            <div className="grid grid-cols-2 gap-2">
                                <ProjRow
                                    label="Principal"
                                    value={`$${formatUsdcCompact(projected.principal)}`}
                                    hint="Returned"
                                />
                                <ProjRow
                                    label="Annual Interest"
                                    value={`$${formatUsdcCompact(projected.annualInterest)}`}
                                    hint={projected.aprHint}
                                    accent
                                />
                            </div>
                            <ProjRow
                                label="Total Payout"
                                value={`$${formatUsdcCompact(projected.total)}`}
                                hint="At maturity"
                                bold
                            />
                        </div>
                    </div>

                    {/* Capital Routing — product-aware. Standard shows the
                        literal 80/10/9/1 split; Turbo shows the leverage-
                        adjusted 50/40/9/1 (see ROUTING_BPS) so the card
                        reflects the loop's amplified Token Buy. */}
                    <div className="flex flex-1 flex-col rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                        <div className="flex items-center justify-between">
                            <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                                Capital Routing
                            </h3>
                            <span className="text-[9px] font-semibold uppercase tracking-widest text-[#e6c985]/70">
                                {formatBps(split.bps.safeVault)} / {formatBps(split.bps.tokenBuy)} / {formatBps(split.bps.treasury)} / {formatBps(split.bps.liquidity)}
                            </span>
                        </div>

                        <div className="mt-2.5 flex h-2 w-full overflow-hidden rounded-full ring-1 ring-[#bfb67b]">
                            <span className="h-full" style={{ width: `${split.bps.safeVault / 100}%`, backgroundColor: "#1D3E29" }} />
                            <span className="h-full" style={{ width: `${split.bps.tokenBuy / 100}%`, backgroundColor: "#FBC206" }} />
                            <span className="h-full" style={{ width: `${split.bps.treasury / 100}%`, backgroundColor: "#5C7A66" }} />
                            <span className="h-full" style={{ width: `${split.bps.liquidity / 100}%`, backgroundColor: "#bfb67b" }} />
                        </div>

                        <ul className="mt-3 grid flex-1 auto-rows-fr grid-cols-2 gap-x-4 gap-y-2.5">
                            <RouteRow
                                color="#1D3E29"
                                label="Safe Vault"
                                pct={`${formatBps(split.bps.safeVault)}%`}
                                amount={formatUsdcCompact(split.safeVault)}
                            />
                            <RouteRow
                                color="#FBC206"
                                label="Token Buy"
                                pct={`${formatBps(split.bps.tokenBuy)}%`}
                                amount={formatUsdcCompact(split.tokenBuy)}
                            />
                            <RouteRow
                                color="#5C7A66"
                                label="Distribution Reserve"
                                pct={`${formatBps(split.bps.treasury)}%`}
                                amount={formatUsdcCompact(split.treasury)}
                            />
                            <RouteRow
                                color="#bfb67b"
                                label="Liquidity"
                                pct={`${formatBps(split.bps.liquidity)}%`}
                                amount={formatUsdcCompact(split.liquidity)}
                            />
                        </ul>
                    </div>
                </div>
            </div>

            <FlowModal
                open={flowOpen}
                title="Flow Steps"
                subtitle="Confirm each step in your wallet."
                steps={steps}
            />

            <ResultModal
                open={result !== null}
                type={result?.type}
                title={result?.title || ""}
                message={result?.message || ""}
                primaryAction={
                    result?.type === "success" ? { label: "Go to My Term Deposits", to: "/term-deposits" } : undefined
                }
                onClose={() => setResult(null)}
            />
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Pieces                                                                    */
/* -------------------------------------------------------------------------- */

function TermHighlights({ productMode }) {
    // §V6.2 — product-aware figures. Local picker convention: 1 =
    // Standard, 2 = Turbo. Contract enum uses 0/1 — that mapping
    // happens at openDeposit call time, not here.
    const isTurbo = productMode === 2;
    // §V6.3 — Turbo's headline 27.5% is the LEVERAGED-position APR; each rung
    // (incl. this deposit) accrues 20% and routes 80/10/9/1 like Standard. The
    // 2.5x leverage / 27.5% net comes from the borrow loop (executeTurboLoop).
    const aprPct = isTurbo ? 27.5 : 20;
    const penaltyPct = isTurbo ? 50 : 20;
    // §V6.3 — capital protected = principal returned even on early exit
    // (= 100% − penalty). Standard 80%, Turbo 50%. (The on-chain Safe-Vault
    // routing is 80% for both; this is the worst-case early-exit floor.)
    const protectedPct = 100 - penaltyPct;
    const items = [
        {
            label: "Annual Yield",
            // Turbo's full 27.5% only applies to larger deposits (~$10k+);
            // smaller ones earn less (loop floored at $100 min-deposit), so
            // never show a bare 27.5% — always "Up to". Per client Jun 2026.
            value: isTurbo ? "Up to 27.5%" : `${aprPct}%`,
            unit: "APR",
            delta: "Daily accrual",
            iconSrc: STAT_ICONS.interest
        },
        {
            label: "Term Length",
            value: String(config.protocol.fixedTermDays),
            unit: "Days",
            delta: "≈ 3 years",
            iconSrc: STAT_ICONS.holdings
        },
        {
            label: "Capital Protected",
            value: `${protectedPct}%`,
            unit: "",
            delta: isTurbo ? "On early exit" : "Safe Vault",
            iconSrc: STAT_ICONS.safe
        },
        {
            label: "Early Exit",
            value: `${penaltyPct}%`,
            unit: "",
            delta: "Penalty",
            iconSrc: STAT_ICONS.active
        }
    ];
    return (
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((item) => (
                <StatCard key={item.label} {...item} />
            ))}
        </div>
    );
}

function ProjRow({ label, value, hint, accent = false, bold = false }) {
    return (
        <div className="flex items-center justify-between rounded-[10px] bg-[#162e1f] ring-1 ring-[#bfb67b]/40 px-3 py-2">
            <div className="min-w-0">
                <div className="text-[10.5px] font-semibold text-white">{label}</div>
                <div className="text-[9px] text-[#e6c985]/70">{hint}</div>
            </div>
            <div
                className={`shrink-0 font-manrope ${bold ? "text-[15px] font-bold" : "text-[13px] font-semibold"} ${
                    accent ? "text-[#FBC206]" : "text-white"
                }`}
            >
                {value}
            </div>
        </div>
    );
}

function RouteRow({ color, label, pct, amount }) {
    return (
        <li className="flex items-center justify-between rounded-[8px] bg-[#162e1f] ring-1 ring-[#bfb67b]/40 px-2.5 py-1.5">
            <div className="flex min-w-0 items-center gap-1.5">
                <span
                    className="inline-block h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: color }}
                />
                <span className="truncate text-[10.5px] font-semibold text-white">{label}</span>
                <span className="text-[9px] font-medium text-[#e6c985]/70">{pct}</span>
            </div>
            <div className="shrink-0 text-right">
                <span className="font-manrope text-[11px] font-bold text-white">
                    {amount}
                </span>
                <span className="ml-1 text-[8px] font-semibold text-[#e6c985]/70">
                    {TOKEN_SYMBOL}
                </span>
            </div>
        </li>
    );
}

function Bullet({ children }) {
    return (
        <li className="flex items-start gap-1.5">
            <span className="mt-0.5 inline-flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-[#FBC206] text-[7px] font-bold text-[#10281b]">
                ✓
            </span>
            <span>{children}</span>
        </li>
    );
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Converts a USDC base-unit BigInt (6 decimals) into a plain decimal
 * string suitable for an `<input type="number">` value. Avoids
 * `Number()` so very large balances don't lose precision and never
 * emits scientific notation (which the input would reject).
 */
function baseToUsdcString(amountBigInt) {
    const value = BigInt(amountBigInt || 0);
    if (value === 0n) return "0";
    const decimals = 6n;
    const factor = 10n ** decimals;
    const whole = value / factor;
    const frac = value % factor;
    if (frac === 0n) return whole.toString();
    const fracStr = frac.toString().padStart(Number(decimals), "0").replace(/0+$/, "");
    return `${whole.toString()}.${fracStr}`;
}

function buildInitialSteps(amount, compoundMode = 0, includeApprove = true) {
    const display = amount ? `${amount} ${TOKEN_SYMBOL}` : TOKEN_SYMBOL;
    const steps = [];
    // Approve step only shown when allowance is insufficient. With
    // infinite approval (MaxUint256), this is a one-time prompt per
    // wallet × BondContract pair — subsequent deposits skip the step.
    if (includeApprove) {
        steps.push({
            key: "approve",
            label: `Approving ${TOKEN_SYMBOL}`,
            doneLabel: `Approved ${TOKEN_SYMBOL}`,
            status: "pending"
        });
    }
    steps.push({
        key: "deposit",
        label: `Depositing ${display}`,
        doneLabel: `Deposited ${display}`,
        status: "pending"
    });
    // Off (0) needs no on-chain call — the deposit struct already defaults
    // compoundMode to 0, so we only add a step for Standard / Turbo.
    if (compoundMode !== 0) {
        const modeLabel = compoundMode === 2 ? "Turbo" : "Standard";
        steps.push({
            key: "compound",
            label: `Enabling ${modeLabel} Automated Reinvestment`,
            doneLabel: `${modeLabel} Automated Reinvestment enabled`,
            status: "pending"
        });
    }
    return steps;
}

// §I.6 capital routing — the figures shown in the Capital Routing card.
//
// Standard — the literal on-chain per-deposit split: 80 / 10 / 9 / 1
//   (Safe Vault 80%, Token Buy 10%, Distribution Reserve 9%, Liquidity 1%).
//
// Turbo — the EFFECTIVE, leverage-adjusted routing users see: 50 / 40 / 9 / 1.
//   On-chain each rung still routes 80/10/9/1, but the Turbo loop borrows
//   ~75% against each rung's Safe Vault and spends it buying more $GILD, so
//   across the whole leveraged position the Safe Vault share effectively drops
//   to ~50% while Token Buy rises to ~40%. That amplified token exposure is
//   what produces the net 27.5% APR. Displaying 50/40/9/1 for Turbo (per
//   client, Jul 2026) makes the card reflect the leverage instead of showing
//   the same split as Standard. Distribution (9%) + Liquidity (1%) unchanged.
const ROUTING_BPS = {
    1: { safeVault: 8000, tokenBuy: 1000, treasury: 900, liquidity: 100 }, // Standard (literal split)
    2: { safeVault: 5000, tokenBuy: 4000, treasury: 900, liquidity: 100 }  // Turbo (leverage-adjusted effective split)
};

// Headline APR. Standard 20%; Turbo the leveraged-position up-to-27.5%. The
// deposit preview projects at THIS rate (per client, Jun 2026) so Turbo shows
// Turbo numbers — on-chain each rung still accrues 20% and the net 27.5%
// EMERGES from the leverage loop (20% on ~2.5x gross − 15% on ~1.5x loan).
const DISPLAY_ANNUAL_RATE_BPS = {
    1: 2000,
    2: 2750
};

function previewSplit(usdcInput, productMode = 1) {
    const base = BigInt(Math.round((Number(usdcInput) || 0) * 1_000_000));
    const bps = ROUTING_BPS[productMode] || ROUTING_BPS[1];
    const safeVault = (base * BigInt(bps.safeVault)) / 10000n;
    const tokenBuy = (base * BigInt(bps.tokenBuy)) / 10000n;
    const treasury = (base * BigInt(bps.treasury)) / 10000n;
    // Liquidity is the residue so the four legs sum to exactly `base`
    // and any bps rounding doesn't show as a missing penny.
    const liquidity = base - safeVault - tokenBuy - treasury;
    return { safeVault, tokenBuy, treasury, liquidity, bps };
}

function projectMaturity(usdcInput, productMode = 1) {
    const principal = BigInt(Math.round((Number(usdcInput) || 0) * 1_000_000));
    // §V6.3 — preview at the HEADLINE rate (Standard 20%, Turbo 27.5%) so the
    // Turbo projection reflects Turbo numbers, not the 20% per-rung accrual.
    const bps = DISPLAY_ANNUAL_RATE_BPS[productMode] || 2000;
    const rate = BigInt(bps);
    const days = BigInt(config.protocol.fixedTermDays);
    const maturityInterest = (principal * rate * days) / (10000n * 365n);
    const annualInterest = (principal * rate) / 10000n;
    const aprPct = bps / 100;
    const aprPctStr = aprPct % 1 === 0 ? String(aprPct) : aprPct.toFixed(1);
    return {
        principal,
        // Per-year interest (client: the "Interest" card shows ANNUAL, not the
        // 3-year figure) and the cumulative 3-year interest for Total Payout.
        annualInterest,
        interest: maturityInterest,
        total: principal + maturityInterest,
        annualRateBps: bps,
        displayAnnualRateBps: bps,
        // Turbo's full rate only applies to larger deposits (~$10k+), so its
        // headline is qualified "up to"; Standard is a flat rate.
        aprLabel: productMode === 2 ? `up to ${aprPctStr}` : aprPctStr,
        aprHint: productMode === 2 ? `up to ${aprPctStr}%/yr` : `+${aprPctStr}%/yr`
    };
}

/** Formats a basis-points value as a percent string, trimming trailing
 *  `.0` so 8000 → "80" and 900 → "9". */
function formatBps(bps) {
    const pct = bps / 100;
    const fixed = pct.toFixed(1);
    return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
}

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Polls the ERC-20 allowance until it reflects at least `required`. Public
 * testnet RPCs sometimes return stale reads for a few seconds after a write
 * lands - if we don't wait, the next contract call's pre-flight estimateGas
 * simulates against stale state and reverts with "ERC20: allowance" even
 * though the chain itself is consistent.
 */
async function waitForAllowance(token, owner, spender, required, timeoutMs = 30000, intervalMs = 1500) {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
        const current = await token.allowance(owner, spender);
        if (current >= required) return current;
        await delay(intervalMs);
    }
    throw new Error("Approval not yet visible on-chain. Please retry the deposit in a few seconds.");
}

/**
 * Polls `getDeposit` until the just-opened deposit reads back on-chain.
 *
 * After `openDeposit` is mined, the wallet still has to simulate the
 * follow-up `setCompoundMode` call (eth_estimateGas) — and the RPC node
 * it picks for that can briefly lag behind the block the deposit landed
 * in. Simulating against that stale block reverts inside
 * `_existingDeposit`, which is the intermittent "Enabling Turbo
 * Automated Reinvestment" failure. Blocking here until the deposit is visible
 * gives every RPC time to converge. Best-effort: on timeout we return
 * false and let the caller try anyway rather than hard-failing.
 */
async function waitForDepositVisible(bond, depositId, timeoutMs = 20000, intervalMs = 1000) {
    const ZERO = "0x0000000000000000000000000000000000000000";
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
        try {
            const d = await bond.getDeposit(depositId);
            if (d && d.owner && d.owner.toLowerCase() !== ZERO) return true;
        } catch {
            // deposit not indexable on this node yet — keep polling
        }
        await delay(intervalMs);
    }
    return false;
}

/**
 * Persist a freshly opened deposit to the backend.
 *
 * AWAITABLE: the caller `await`s this before showing the success modal,
 * so the deposit row is in Mongo by the time the user navigates to
 * My Term Deposits — no waiting for the next indexer tick. Failures are
 * swallowed (logged only): the indexer remains the long-term source of
 * truth and the row converges within one poll cycle even if this POST
 * fails, so a backend hiccup must never fail the (successful) deposit.
 *
 * The depositId is parsed from the `DepositOpened` event in the receipt;
 * if that fails it falls back to `nextDepositId() - 1`.
 */
async function recordDepositToBackend({ bond, depositId: knownId, receipt, owner, amountWei, txHash }) {
    try {
        const depositId = knownId || (await resolveDepositId(bond, receipt));
        if (!depositId) return;
        const onchain = await bond.getDeposit(depositId);
        await upsertDeposit({
            depositId,
            owner: owner.toLowerCase(),
            principal: amountWei.toString(),
            accruedInterest: onchain.accruedInterest.toString(),
            loanBalance: onchain.loanBalance.toString(),
            startTime: Number(onchain.startTime),
            maturityTime: Number(onchain.maturityTime),
            state: Number(onchain.state),
            compoundMode: Number(onchain.compoundMode),
            // §V6.2 — productMode field on the on-chain Deposit struct.
            // Fast-paths the Turbo / Standard pill onto the dashboard
            // without waiting for the indexer's next tick.
            productMode: Number(onchain.productMode ?? 0),
            txHash
        });
    } catch (err) {
        // Indexer will eventually save it — don't surface this to the user.
        console.warn("Backend upsert failed; relying on indexer:", err?.message || err);
    }
}

async function resolveDepositId(bond, receipt) {
    if (receipt?.logs) {
        for (const log of receipt.logs) {
            try {
                const parsed = bond.interface.parseLog(log);
                if (parsed?.name === "DepositOpened") {
                    return Number(parsed.args.depositId);
                }
            } catch {
                // Not the DepositOpened topic — skip.
            }
        }
    }
    // Fallback: contract counter is monotonically increasing and points at
    // the next id, so the just-opened deposit is N-1.
    try {
        const next = await bond.nextDepositId();
        const id = Number(next) - 1;
        return id > 0 ? id : null;
    } catch {
        return null;
    }
}

function ChevronRightSmall() {
    return (
        <svg
            className="h-3 w-3"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <polyline points="7 4 13 10 7 16" />
        </svg>
    );
}
